import type { StitchRef } from "../model/Project";
import { createStitchMatch } from "../model/stitchMatch";
import type { EditorContext } from "../tools/EditorContext";

/**
 * Shared "match to another seam" pick state (#199). Arming the pick (from the
 * properties panel's "Match to another seam" button) records the seam that will
 * FOLLOW; the next seam the user picks — by clicking it on the canvas OR by
 * selecting it in the layers / parts list — becomes the controlling MAIN.
 *
 * Lifting the armed source out of `StitchTool` lets both the canvas and the
 * layers panel complete the same pick. Listeners (the tool's cursor/toast, the
 * layers panel's affordance highlight) react to arm / cancel.
 */
let source: StitchRef | null = null;
const listeners = new Set<() => void>();

function emit() {
  for (const fn of [...listeners]) fn();
}

export const stitchMatchPick = {
  /** The seam that will follow once a target is picked, or null when idle. */
  get source(): StitchRef | null {
    return source;
  },
  isArmed(): boolean {
    return source !== null;
  },
  /** True when `ref` is the armed (follower-to-be) seam itself. */
  isSource(ref: StitchRef): boolean {
    return source != null && source.stitchId === ref.stitchId;
  },
  arm(ref: StitchRef) {
    source = ref;
    emit();
  },
  cancel() {
    if (source !== null) {
      source = null;
      emit();
    }
  },
  subscribe(fn: () => void): () => void {
    listeners.add(fn);
    return () => {
      listeners.delete(fn);
    };
  },
  /**
   * Complete the pick against `target` (the chosen MAIN). Commits the match,
   * selects the follower, and disarms. A click on the armed seam itself just
   * cancels. Returns true when a match was created.
   */
  complete(ctx: EditorContext, target: StitchRef): boolean {
    const src = source;
    if (!src) return false;
    source = null;
    emit();
    if (src.stitchId === target.stitchId) return false;
    // `src` is the FOLLOWER; `target` is the MAIN whose count it adopts.
    ctx.commit("Match stitch lines", (proj) =>
      createStitchMatch(proj, target, src),
    );
    ctx.setSelection({
      pieceId: src.pieceId,
      decorationId: null,
      stitchId: src.stitchId,
    });
    return true;
  },
};
